/**
 * Chat message form. Owns the Trix editor and inserts story attachments into it.
 * Scoped to #message_form so it reconnects fresh on every turbo-stream conversation
 * switch (the "conversations" partial is replaced on nav). Exposes `attachStory` for
 * the chat-story-search controller to call via a Stimulus outlet.
 *
 * Example: { data: {controller: "forms--chat-message-form"} }
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  get editor() {
    return this.trixInputTarget.editor;
  }

  static targets = ["trixInput"];

  connect() {
    this.attachStoryViaUrlParam();
    this.form = this.element.querySelector("form");

    this.boundPreventIOSKeyboardScroll = this.preventIOSKeyboardScroll.bind(this);
    if (this.hasTrixInputTarget) {
      this.trixInputTarget.addEventListener("focus", this.boundPreventIOSKeyboardScroll);
    }
  }

  disconnect() {
    if (this.hasTrixInputTarget) {
      this.trixInputTarget.removeEventListener("focus", this.boundPreventIOSKeyboardScroll);
    }
  }

  attachStoryViaUrlParam() {
    let urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has("attach_sgid")) {
      this.attachStory(urlParams.get("attach_sgid"), true);

      // Remove param from URL to prevent attaching story again after message form submits
      let newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("attach_sgid");
      window.history.replaceState({}, document.title, newUrl);
    }
  }

  attachStory(sgid, insert_newline = false) {
    fetch(`/search/embed.json?` + new URLSearchParams({ sgid: sgid }))
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error("Sgid does not exist.");
      })
      .then((embed) => this.createStoryAttachment(embed, insert_newline))
      .catch((error) => {
        console.log(error);
      });
  }

  // show the form and focus it. to be called as an outlet, from a different
  // controller that knows when the conversation is loaded
  unhide() {
    this.element.classList.remove("d-none");
    this.trixInputTarget.focus();
  }

  createStoryAttachment(embed, insert_newline = false) {
    const attachment = new Trix.Attachment({
      sgid: embed.sgid,
      content: embed.content,
      contentType: "text/story",
    });

    this.editor.insertAttachment(attachment);
    this.editor.insertString(insert_newline ? "\n" : " ");
  }

  refocus() {
    this.trixInputTarget.focus();
  }

  // Since form has multiple submit buttons, ensure all are re-enabled post-submit.
  // Without this, submit buttons other than the one that triggered the submission
  // become disabled indefinitely due to how UJS operates.
  handleSubmitEnd() {
    const submitButtons = this.form.querySelectorAll("input[type='submit']");
    submitButtons.forEach((button) => {
      button.disabled = false;
    });
  }

  // Prevents iOS keyboard opening from scrolling the chat container off screen.
  // iOS will always try to centre an input on focus, with a few exceptions, one
  // being if the focused input has 0 opacity. So we turn the input's opacity to
  // 0 for a single frame, preventing the scrolling behaivour with minimal visual
  // impact.
  preventIOSKeyboardScroll() {
    this.trixInputTarget.style.opacity = 0;
    setTimeout(() => (this.trixInputTarget.style.opacity = 1));
  }
}
